import { useEffect, useState } from 'react';

import './ImageWithSkeleton.css';

/**
 * Imagem remota que exibe um esqueleto animado enquanto carrega.
 *
 * Existe porque foto vinda do servidor (logo da empresa, foto do profissional) chega
 * depois do restante da tela: sem placeholder, o cliente vê um buraco no card e a
 * imagem "aparecendo do nada" a cada passo do agendamento. O esqueleto ocupa o mesmo
 * espaço da imagem final, então nada se desloca quando ela entra.
 *
 * O erro continua sendo do chamador: aqui só avisamos via onError, porque cada tela
 * tem um fallback diferente (iniciais do nome, logo padrão do sistema).
 *
 * @param {string} src URL da imagem.
 * @param {string} alt Texto alternativo.
 * @param {string} className Classes da própria <img>, preservando o CSS de cada tela.
 * @param {string} wrapperClassName Classes do container que posiciona o esqueleto.
 * @param {boolean} fill Faz o container ocupar todo o espaço do pai. Use quando o pai
 *                       já define o tamanho (caixa da logo, moldura quadrada da foto).
 * @param {boolean} onDark Inverte o shimmer para fundo escuro.
 * @param {Function} onError Chamado quando a imagem falha, para a tela decidir o fallback.
 */
const ImageWithSkeleton = ({
    src,
    alt,
    className = '',
    wrapperClassName = '',
    fill = false,
    onDark = false,
    onError,
    ...imgProps
}) => {
    const [loaded, setLoaded] = useState(false);

    // Trocar de profissional ou de empresa reaproveita este mesmo nó: sem resetar,
    // a imagem nova entraria já marcada como carregada e nasceria sem esqueleto.
    useEffect(() => {
        setLoaded(false);
    }, [src]);

    // Imagem que veio do cache do navegador pode terminar de carregar antes do React
    // registrar o onLoad. O complete do próprio elemento cobre esse caso.
    const handleRef = (element) => {
        if (element?.complete) {
            setLoaded(true);
        }
    };

    const handleError = (event) => {
        // Marca como carregada para o esqueleto não ficar girando eternamente caso a
        // tela chamadora opte por manter a imagem quebrada em vez de trocar por outra.
        setLoaded(true);

        if (onError) {
            onError(event);
        }
    };

    return (
        <span className={`image-skeleton ${fill ? 'image-skeleton--fill' : ''} ${wrapperClassName}`.trim()}>
            {!loaded && (
                <span
                    className={`image-skeleton__shimmer ${onDark ? 'image-skeleton__shimmer--on-dark' : ''}`.trim()}
                    aria-hidden="true"
                />
            )}
            <img
                {...imgProps}
                ref={handleRef}
                src={src}
                alt={alt}
                className={`image-skeleton__img ${loaded ? 'image-skeleton__img--loaded' : ''} ${className}`.trim()}
                onLoad={() => setLoaded(true)}
                onError={handleError}
            />
        </span>
    );
};

export default ImageWithSkeleton;
